import { useState } from 'react'
import { Mail, MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react'

import {
  CAMPAIGN_CHANNEL_LABELS,
  TEMPLATE_TRIGGER_LABELS,
  type CampaignChannel,
  type CampaignTemplate,
  type TemplateTrigger,
} from '@/types/marketing'
import {
  useCreateCampaignTemplate,
  useCampaignTemplates,
  useDeleteCampaignTemplate,
  useUpdateCampaignTemplate,
} from '@/features/marketing/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const CHANNEL_OPTIONS = Object.entries(CAMPAIGN_CHANNEL_LABELS)

export function TemplatesTab() {
  const { data: templates = [], isLoading } = useCampaignTemplates()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CampaignTemplate | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Reusable message content with {'{{first_name}}'}-style placeholders, rendered per
          recipient at send time.
        </p>
        <Button
          onClick={() => {
            setEditing(null)
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No templates yet — create one to build campaigns from.
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="gap-1">
                        {t.channel === 'email' ? (
                          <Mail className="h-3 w-3" />
                        ) : (
                          <MessageSquare className="h-3 w-3" />
                        )}
                        {CAMPAIGN_CHANNEL_LABELS[t.channel]}
                      </Badge>
                      {t.trigger_type && (
                        <Badge variant="secondary">
                          {TEMPLATE_TRIGGER_LABELS[t.trigger_type as TemplateTrigger]}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-sm">
                    <div className="truncate text-xs text-muted-foreground">
                      {t.channel === 'email' && t.subject ? `${t.subject} — ` : ''}
                      {t.body.split('\n')[0]}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditing(t)
                          setDialogOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TemplateDeleteButton template={t} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TemplateDialog open={dialogOpen} onOpenChange={setDialogOpen} template={editing} />
    </div>
  )
}

function TemplateDeleteButton({ template }: { template: CampaignTemplate }) {
  const deleteMutation = useDeleteCampaignTemplate()
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setConfirmOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              “{template.name}” is removed. Campaigns already sent keep their snapshot of the
              content.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                await deleteMutation.mutateAsync(template.id)
                setConfirmOpen(false)
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface TemplateFormState {
  name: string
  channel: CampaignChannel
  subject: string
  body: string
  trigger_type: TemplateTrigger | ''
}

const EMPTY_FORM: TemplateFormState = {
  name: '',
  channel: 'email',
  subject: '',
  body: '',
  trigger_type: '',
}

function TemplateDialog({
  open,
  onOpenChange,
  template,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: CampaignTemplate | null
}) {
  const createMutation = useCreateCampaignTemplate()
  const updateMutation = useUpdateCampaignTemplate()
  const pending = createMutation.isPending || updateMutation.isPending

  const [form, setForm] = useState<TemplateFormState>(EMPTY_FORM)

  // Reset the form whenever the dialog opens or the target template changes.
  // Implemented as "adjust state during render" (see React docs) to avoid a
  // setState-in-effect cascading render.
  const formKey = `${open}:${template?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      setForm(
        template
          ? {
              name: template.name,
              channel: template.channel,
              subject: template.subject ?? '',
              body: template.body,
              trigger_type: template.trigger_type ?? '',
            }
          : EMPTY_FORM,
      )
    }
  }

  const canSave = form.name.trim().length > 0 && form.body.trim().length > 0

  const handleSubmit = async () => {
    if (!canSave) return
    const body = {
      name: form.name.trim(),
      channel: form.channel,
      subject: form.channel === 'email' ? form.subject.trim() : '',
      body: form.body,
      trigger_type: form.trigger_type || undefined,
    }
    try {
      if (template) await updateMutation.mutateAsync({ id: template.id, body })
      else await createMutation.mutateAsync(body)
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast; keep dialog open
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'New Template'}</DialogTitle>
          <DialogDescription>
            Placeholders:{' '}
            <code className="rounded bg-muted px-1 text-xs">
              {'{{first_name}} {{last_name}} {{company}}'}
            </code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                placeholder="Winterization special"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Channel</label>
              <Select
                value={form.channel}
                onValueChange={(v) => setForm((f) => ({ ...f, channel: v as CampaignChannel }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.channel === 'email' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Input
                placeholder="Beat the rush — winterization special"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Auto-use (inventory alerts)</label>
            <Select
              value={form.trigger_type || 'manual'}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  trigger_type: v === 'manual' ? '' : (v as TemplateTrigger),
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual campaigns only</SelectItem>
                <SelectItem value="new_listing">{TEMPLATE_TRIGGER_LABELS.new_listing}</SelectItem>
                <SelectItem value="price_drop">{TEMPLATE_TRIGGER_LABELS.price_drop}</SelectItem>
              </SelectContent>
            </Select>
            {form.trigger_type && (
              <p className="text-xs text-muted-foreground">
                Boat placeholders{' '}
                <code className="rounded bg-muted px-1">
                  {'{{boat_name}} {{make}} {{model}} {{price}}'}
                </code>{' '}
                render when the alert fires. Only one template per trigger.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Body</label>
            <Textarea
              rows={8}
              placeholder={'Hi {{first_name}},\n\n…'}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
            {form.channel === 'sms' && (
              <p className="text-xs text-muted-foreground">
                A “Reply STOP to opt out” footer is appended automatically.
              </p>
            )}
            {form.channel === 'email' && (
              <p className="text-xs text-muted-foreground">
                An unsubscribe footer is appended automatically.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending || !canSave}>
            {template ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
